import React, { memo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Text } from '@atlaskit/primitives/compiled';
import { fg } from '@atlassian/jira-feature-gating';
import { ParentInlineEditViewIsEditable } from '@atlassian/jira-issue-field-parent-inline-edit-full/src/ui/parent/index.tsx';
import type { ParentCell$key } from '@atlassian/jira-relay/src/__generated__/ParentCell.graphql';
import type { ParentCell_inlineEdit$key } from '@atlassian/jira-relay/src/__generated__/ParentCell_inlineEdit.graphql';
import { FormattedMessage } from '@atlassian/jira-intl';
import {
	useIsDensityFull,
	useIsInlineEditingExtendedFieldSupportEnabled,
	useIsSingleLineRowHeightEnabled,
} from '../../../../controllers/features/selectors.tsx';
import { useLazyLoadEditability } from '../../../../services/lazy-editability-loader/index.tsx';
import { INLINE_EDIT_ANALYTICS_ATTRIBUTES } from '../common/inline-edit-analytics-attributes.tsx';
import messages from './messages.tsx';

export type Props = {
	fieldRef: ParentCell$key;
	fieldType: string;
	isReadOnlyWithoutHoverPopover?: boolean;
};

export const ParentCell = memo<Props>(
	({ fieldRef, fieldType = '', isReadOnlyWithoutHoverPopover }) => {
		const data = useFragment<ParentCell$key>(
			graphql`
				fragment ParentCell on JiraParentIssueField
				@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
					id
					...ParentCell_inlineEdit @arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
				}
			`,
			fieldRef,
		);

		const isDensityFull = useIsDensityFull();

		const isInlineEditingExtendedFieldSupportEnabled =
			useIsInlineEditingExtendedFieldSupportEnabled();

		const isSingleLineRowHeightEnabled = useIsSingleLineRowHeightEnabled();

		/**
		 * As part of the initiative to remove "Epic Link" and "Parent Link" fields from Jira,
		 * we're rendering a "Replaced by Parent field" deprecation message in the "Parent Link" cell.
		 * At the time of writing this comment, "Parent Link" is still used in ~1% of issue tables, and
		 * we hope this message will help these users migrate and use the "Parent" field from now on.
		 */
		if (
			// eslint-disable-next-line jira/ff/no-preconditioning
			fieldType === 'com.atlassian.jpo:jpo-custom-field-parent' &&
			fg('replace-epic-parent-link-by-depreca-message-in-nin') &&
			!fg('jsc_fix_parent_cell_hooks_violation')
		) {
			return (
				<Text as="span" color="color.text.subtlest">
					<FormattedMessage
						{...messages.replacedByParentField}
						values={{
							strong: (chunks) => <Text as="strong">{chunks}</Text>,
						}}
					/>
				</Text>
			);
		}

		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const inlineEditData = useFragment<ParentCell_inlineEdit$key>(
			graphql`
				fragment ParentCell_inlineEdit on JiraParentIssueField
				@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
					id
					lazyIsEditableInIssueView @include(if: $isInlineEditingEnabled)
					...parent_issueFieldParentInlineEditFull_ParentInlineEditViewIsEditable_fragmentRef
				}
			`,
			data,
		);

		// If the property does not exist in fragment data then we know it has been conditionally excluded because inline
		// editing is disabled. In this case we explicitly set isEditable to false to bypass the lazy editability query.
		const isEditable =
			'lazyIsEditableInIssueView' in inlineEditData && isInlineEditingExtendedFieldSupportEnabled
				? inlineEditData.lazyIsEditableInIssueView
				: false;
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useLazyLoadEditability(inlineEditData.id, isEditable);

		return (
			<ParentInlineEditViewIsEditable
				attributes={INLINE_EDIT_ANALYTICS_ATTRIBUTES}
				spacing="default"
				editViewPopup
				editViewPopupAlignBlock="center"
				fragmentRef={inlineEditData}
				isTruncated={!isDensityFull || isSingleLineRowHeightEnabled}
				isEditable={(isEditable && !isReadOnlyWithoutHoverPopover) ?? false}
				menuPosition="fixed"
				readViewFitContainerHeight
				// Render an empty string when there is no parent and experiment is off to mimic FG off state
				emptyText={isInlineEditingExtendedFieldSupportEnabled ? undefined : ''}
				isSingleLineRowHeightEnabled={
					isSingleLineRowHeightEnabled && !isReadOnlyWithoutHoverPopover
				}
			/>
		);
	},
);
